import { PersonBenefits } from '@esportal/portal-service';
import {
	AspectRatioContainer,
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Image,
	Layout,
	LayoutRowGap,
	Select,
	Spinner,
	styled,
	Text,
	TextAlignment,
	TokenTextAppearance,
	TokenTextColor,
} from '@volkswagen-onehub/components-core';
import React from 'react';
import { PortalContext } from '../../../../context/portal-context';
import { COLOR_TOKEN } from '../../../../helpers/color-tokens';
import { getParamsClubVW } from '../../../../helpers/get-params-clubvw';
import { useErrorAlert } from '../../../../hooks/use-error-alert';
import { useTrackingManager } from '../../../../tracking/use-tracking-manager';
import { TelefonoInput } from '../../../oneform/components/telefono-input';
import { DefaultPromotionData } from '../../promotion-popup-layer';
import { GTIDayParticipado } from './gti-day-layer-participado';

const LayerContentWrapper = styled.div`
	width: 100%;
	max-width: 700px;
	margin: 0 auto;
	& * {
		text-wrap: balance;
	}
`;

const GreyContainer = styled(Container)`
	background-color: ${COLOR_TOKEN.background.disabled};
`;

interface GTIDayForm {
	name: string;
	data: DefaultPromotionData & {
		companion: 'Sí' | 'No';
	};
}

type CompanionOption = '' | GTIDayForm['data']['companion'];

export const GTIDayParticipa: React.FC = () => {
	const { portalService } = React.useContext(PortalContext);
	const { device, tc, url } = getParamsClubVW();
	const trackingManager = useTrackingManager();
	const [participando, setParticipando] = React.useState<boolean>(false);
	const [estadoParticipacion, setEstadoParticipacion] = React.useState<'participa' | 'participado'>('participa');
	const [phoneData, setPhoneData] = React.useState({ prefix: '+34', phoneNumber: '' });
	const [hasCompanion, setHasCompanion] = React.useState<CompanionOption>('');
	const { showAlert } = useErrorAlert();

	const promotionName = 'GTIDay2026';

	React.useEffect(() => {
		trackingManager.trackClubVWFormLoad(
			{
				FormName: 'vw-club-GTIDay2026-enroll',
				FormStart: true,
				FormTypeMultiple: ['registration'],
			},
			{ contentId: 'vw-club-GTIDay2026-enroll' },
			{ vehicle: [] },
		);
	}, []);

	const handleParticipate = () => {
		if (hasCompanion !== 'Sí' && hasCompanion !== 'No') {
			return;
		}

		const participateData: GTIDayForm = {
			name: promotionName,
			data: {
				phonePrefix: phoneData.prefix,
				phoneMobile: phoneData.phoneNumber,
				originDeviceType: device,
				originTrackingCode: tc,
				formUrl: url,
				companion: hasCompanion,
			},
		};

		setParticipando(true);

		portalService.postParticipate(participateData).subscribe({
			next: (response: PersonBenefits | null) => {
				setParticipando(false);

				if (response !== null && response.benefits.promotions) {
					const responsePromotion = response.benefits.promotions.find((p) => p.name === promotionName);
					if (responsePromotion?.contestant) {
						setEstadoParticipacion('participado');
						trackingManager.trackClubVWFormSubmissionSuccessMessageLoad(
							{
								FormName: 'vw-club-GTIDay2026-enroll',
								FormStart: false,
								FormTypeMultiple: ['registration'],
							},
							{ contentId: 'vw-club-GTIDay2026-enroll' },
							{ vehicle: [] },
						);
					}
				} else {
					showAlert();
				}
			},
			error: () => {
				setParticipando(false);
				showAlert();
			},
		});
	};

	if (estadoParticipacion === 'participado') {
		return <GTIDayParticipado />;
	}

	const isPhoneValid = phoneData.phoneNumber.trim() !== '';
	const hasSelectedCompanion = hasCompanion === 'Sí' || hasCompanion === 'No';
	const isFormValid = isPhoneValid && hasSelectedCompanion;

	return (
		<Container
			padding={{
				right: ContainerPadding.dynamic0100,
				bottom: ContainerPadding.dynamic0100,
				left: ContainerPadding.dynamic0100,
			}}
			horizontalAlign={'center'}
			wrap={'always'}
		>
			<AspectRatioContainer data-testid="aspect-ratio-container" ratio="2:1">
				<Image
					src={
						'https://assets.volkswagen.com/is/image/volkswagenag/alerta_open_50_golf_gti?Zml0PWNyb3AlMkMxJndpZD0xNjAwJmhlaT0xMjAwJmZtdD1wbmctYWxwaGEmYmZjPW9mZiY4ZTMw'
					}
					alt="Imagen La Liga"
					style={{ borderRadius: '16px', objectFit: 'cover' }}
				/>
			</AspectRatioContainer>
			<LayerContentWrapper>
				<Container
					wrap={'always'}
					gutter={ContainerGutter.dynamic0100}
					padding={{
						top: ContainerPadding.dynamic0130,
						bottom: ContainerPadding.dynamic0100,
					}}
				>
					<Text appearance={TokenTextAppearance.headline300} textAlign={TextAlignment.center}>
						<Text bold>Te regalamos 2 entradas</Text> para el GTI 50 Fest
					</Text>
					<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
						Este año celebramos medio siglo de pasión GTI en un evento único y, si algo tenemos claro, es que esta
						celebración no sería lo mismo sin ti. Solo unos pocos podrán decir que conducen un Golf GTI 50 Aniversario y
						tú ya formas parte de nuestra historia.
					</Text>
				</Container>
			</LayerContentWrapper>
			<GreyContainer
				stretchContent
				padding={{
					top: ContainerPadding.dynamic0050,
					bottom: ContainerPadding.dynamic0050,
					left: ContainerPadding.dynamic0050,
					right: ContainerPadding.dynamic0050,
				}}
			>
				<LayerContentWrapper>
					<Container wrap={'always'} gutter={ContainerGutter.dynamic0100}>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							Por eso, te regalamos 2 entradas para el GTI 50 Fest:
							<br />
							<Text bold>→ 1 entrada Drive GTI para ti</Text>
							<br />
							<Text bold>→ 1 entrada general para tu acompañante</Text>
						</Text>
					</Container>
				</LayerContentWrapper>
			</GreyContainer>
			<LayerContentWrapper>
				<Container
					wrap={'always'}
					gutter={ContainerGutter.dynamic0100}
					padding={{
						top: ContainerPadding.dynamic0130,
					}}
				>
					<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
						Será el sábado 27 de junio, de 10h a 20h, en el Circuit de Barcelona&#8209;Catalunya. Si quieres saber más
						sobre el evento, tienes todos los detalles{' '}
						<CTA tag="a" emphasis="tertiary" href="https://gti50fest.com/" target="_blank">
							<Text bold>aquí</Text>
						</CTA>
						.
					</Text>
					<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
						Además, por ser del club, tu entrada incluye:
						<br />— Acceso fast track
						<br />— 20% de descuento en la tienda GTI del evento
						<br />— Welcome pack VIP
					</Text>
					<Container stretchContent>
						<Text appearance={TokenTextAppearance.bigcopy100} textAlign={TextAlignment.center}>
							<Text bold>¿Cómo puedes conseguir tus entradas?</Text>
						</Text>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							Fácil. Solo tienes que dejarnos tu teléfono y nos pondremos en contacto contigo.
						</Text>
					</Container>
				</Container>
			</LayerContentWrapper>
			{/* ------------------------------------------------------------------------------- */}
			<Container
				wrap={'always'}
				gutter={ContainerGutter.dynamic0050}
				padding={{
					top: ContainerPadding.dynamic0130,
					bottom: ContainerPadding.dynamic0100,
				}}
			>
				<Container horizontalAlign={'center'}>
					<Layout
						data-testid="laLigaParticipa__layout"
						appearance={{
							[Breakpoints.default]: [
								{ name: 'a', columns: 24 },
								{ name: 'b', columns: 24 },
							],
							[Breakpoints.b960]: [
								{ name: '.', columns: 1 },
								{ name: 'a', columns: 10 },
								{ name: '.', columns: 1 },
								{ name: 'b', columns: 11 },
								{ name: '.', columns: 1 },
							],
						}}
						rowGap={LayoutRowGap.dynamic0100}
					>
						<Container
							wrap={'always'}
							gutter={ContainerGutter.static200}
							padding={{
								top: ContainerPadding.static100,
								right: ContainerPadding.static100,
								bottom: ContainerPadding.static100,
								left: ContainerPadding.static100,
							}}
						>
							<Text textAlign={TextAlignment.center}>¿Vendrás con acompañante?</Text>
							<Select
								label="Selecciona tu respuesta*"
								value={hasCompanion}
								onChange={(event: React.SyntheticEvent<HTMLSelectElement>) =>
									setHasCompanion(event.currentTarget.value as CompanionOption)
								}
								isFloating
							>
								<option value="" disabled>
									Selecciona tu respuesta
								</option>
								<option value="Sí">Sí</option>
								<option value="No">No</option>
							</Select>
						</Container>
						<Container
							wrap={'always'}
							gutter={ContainerGutter.static200}
							padding={{
								top: ContainerPadding.static100,
								right: ContainerPadding.static100,
								bottom: ContainerPadding.static100,
								left: ContainerPadding.static100,
							}}
						>
							<Text textAlign={TextAlignment.center}>Teléfono</Text>
							<TelefonoInput
								prefix={phoneData.prefix}
								phoneNumber={phoneData.phoneNumber}
								onChange={(data) => setPhoneData(data)}
								required
							/>
						</Container>
					</Layout>
				</Container>
				<Container horizontalAlign={'center'}>
					<CTA
						tag="button"
						aria-label="CTA vouchers redirect"
						size="large"
						disabled={participando || !isFormValid}
						icon={participando ? <Spinner /> : undefined}
						onClick={() => handleParticipate()}
					>
						<Text bold> {participando ? '' : 'Enviar'}</Text>
					</CTA>
				</Container>
				<Container horizontalAlign={'center'}>
					<Text
						appearance={TokenTextAppearance.copy100}
						color={TokenTextColor.secondary}
						textAlign={TextAlignment.center}
					>
						Para poder obtener de forma gratuita una entrada Drive GTI y una entrada general para el GTI 50 Fest es
						necesario ser propietario de un Golf GTI 50 aniversario, ser miembro del Club Volkswagen y cumplimentar
						debidamente el correspondiente formulario de solicitud al cual podrás acceder a través del siguiente enlace{' '}
						<CTA
							tag="a"
							aria-label="CTA bases legales"
							size="large"
							emphasis="tertiary"
							onClick={() => {
								window.open('https://www.volkswagen.es/es/clientes/mi-club-volkswagen.html', '_blank');
							}}
						>
							<Text>https://www.volkswagen.es/es/clientes/mi-club-volkswagen.html</Text>
						</CTA>
						. Promoción válida hasta fin de existencias.
					</Text>
				</Container>
			</Container>
		</Container>
	);
};
